import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../services/api';
import {
  LinkIcon,
  ArrowPathIcon,
  TrashIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  LockClosedIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';

const FLEXPA_PUBLISHABLE_KEY = process.env.REACT_APP_FLEXPA_PUBLISHABLE_KEY || '';

export default function ConnectedProviders({ isSubscriber = false }) {
  const navigate = useNavigate();
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(null);   // connection id being synced
  const [removing, setRemoving] = useState(null); // connection id being removed
  const [status, setStatus] = useState(null);     // { type: 'success'|'error', message }
  const [linkReady, setLinkReady] = useState(false);
  const [showDisclosure, setShowDisclosure] = useState(false);

  // Load Flexpa Link JS once on mount
  useEffect(() => {
    if (window.FlexpaLink) { setLinkReady(true); return; }
    const script = document.createElement('script');
    script.src = 'https://js.flexpa.com/v1/index.js';
    script.async = true;
    script.onload = () => setLinkReady(true);
    script.onerror = () => console.error('Failed to load Flexpa Link');
    document.head.appendChild(script);
    return () => {
      // Leave script in DOM — removing it would break future re-renders
    };
  }, []);

  const fetchConnections = useCallback(async () => {
    try {
      const res = await api.get('/health-connections');
      setConnections(res.data);
    } catch (err) {
      console.error('Failed to load health connections:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchConnections(); }, [fetchConnections]);

  const handleConnect = () => {
    setStatus(null);
    setShowDisclosure(true);
  };

  const proceedWithFlexpa = () => {
    setShowDisclosure(false);
    if (!FLEXPA_PUBLISHABLE_KEY) {
      setStatus({ type: 'error', message: 'Flexpa integration is not yet configured. Please contact support.' });
      return;
    }
    if (!linkReady || !window.FlexpaLink) {
      setStatus({ type: 'error', message: 'Provider login is still loading. Please try again in a moment.' });
      return;
    }

    window.FlexpaLink.create({
      publishableKey: FLEXPA_PUBLISHABLE_KEY,
      onSuccess: async ({ publicToken }) => {
        try {
          setStatus({ type: 'success', message: 'Connecting to your health provider…' });
          await api.post('/health-connections/exchange', { publicToken });
          await fetchConnections();
          setStatus({ type: 'success', message: 'Health provider connected! Click Sync to import your latest labs.' });
        } catch (err) {
          setStatus({ type: 'error', message: err.response?.data?.error || 'Failed to connect provider. Please try again.' });
        }
      },
      onExit: () => {
        // User closed the modal without connecting — no action needed
      },
    });
    window.FlexpaLink.open();
  };

  const handleSync = async (conn) => {
    setSyncing(conn.id);
    setStatus(null);
    try {
      setStatus({ type: 'success', message: `Syncing from ${conn.providerName} and analyzing your results…` });
      const res = await api.post(`/health-connections/${conn.id}/sync`);
      await fetchConnections();
      if (res.data.sessionId) {
        // Analysis was auto-generated — navigate straight to the results
        navigate(`/app/analysis/${res.data.sessionId}`);
      } else {
        setStatus({ type: 'success', message: res.data.message });
      }
    } catch (err) {
      setStatus({ type: 'error', message: err.response?.data?.error || 'Sync failed. Please try again.' });
    } finally {
      setSyncing(null);
    }
  };

  const handleDisconnect = async (conn) => {
    if (!window.confirm(`Disconnect from ${conn.providerName}? Your imported lab history will be preserved.`)) return;
    setRemoving(conn.id);
    setStatus(null);
    try {
      await api.delete(`/health-connections/${conn.id}`);
      setConnections((prev) => prev.filter((c) => c.id !== conn.id));
      setStatus({ type: 'success', message: `${conn.providerName} disconnected.` });
    } catch (err) {
      setStatus({ type: 'error', message: err.response?.data?.error || 'Failed to disconnect.' });
    } finally {
      setRemoving(null);
    }
  };

  return (
    <section className="mt-8 pt-8 border-t border-slate-200">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-800">Connected Health Providers</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Link your healthcare provider or insurance portal to automatically import your lab results.
          </p>
        </div>
        {isSubscriber && (
          <button
            onClick={handleConnect}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-sky-600 text-white text-sm font-medium hover:bg-sky-700 transition-colors"
          >
            <LinkIcon className="h-4 w-4" />
            Connect Provider
          </button>
        )}
      </div>

      {/* Locked state for non-subscribers */}
      {!isSubscriber ? (
        <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-6 text-center">
          <div className="w-12 h-12 rounded-full bg-slate-200 flex items-center justify-center mx-auto mb-3">
            <LockClosedIcon className="w-5 h-5 text-slate-400" />
          </div>
          <p className="text-sm font-medium text-slate-600 mb-1">Premium Feature</p>
          <p className="text-xs text-slate-400 mb-4 max-w-xs mx-auto">
            Connect your insurance portal or EHR (Epic, Cerner, etc.) to automatically import lab results. Available with a paid subscription.
          </p>
          <Link
            to="/pricing"
            className="inline-flex items-center gap-1.5 bg-sky-600 hover:bg-sky-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
          >
            <SparklesIcon className="w-4 h-4" />
            Unlock with subscription
          </Link>
        </div>
      ) : (
        <>
          {/* Status message */}
          {status && (
            <div
              className={`flex items-start gap-2 p-3 rounded-lg text-sm mb-4 ${
                status.type === 'success'
                  ? 'bg-green-50 text-green-800 border border-green-200'
                  : 'bg-red-50 text-red-800 border border-red-200'
              }`}
            >
              {status.type === 'success' ? (
                <CheckCircleIcon className="h-4 w-4 mt-0.5 flex-shrink-0" />
              ) : (
                <ExclamationCircleIcon className="h-4 w-4 mt-0.5 flex-shrink-0" />
              )}
              <span>{status.message}</span>
            </div>
          )}

          {/* Connection list */}
          {loading ? (
            <p className="text-sm text-slate-400">Loading…</p>
          ) : connections.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-300 p-6 text-center">
              <LinkIcon className="h-8 w-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">No providers connected yet.</p>
              <p className="text-xs text-slate-400 mt-1">
                Connect your insurance portal or EHR (Epic, Cerner, etc.) to automatically pull your lab results.
              </p>
            </div>
          ) : (
            <ul className="space-y-3">
              {connections.map((conn) => (
                <li
                  key={conn.id}
                  className="flex items-center justify-between p-4 bg-white rounded-xl border border-slate-200 shadow-sm"
                >
                  <div>
                    <p className="text-sm font-medium text-slate-800">{conn.providerName}</p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {conn.lastSyncedAt
                        ? `Last synced ${new Date(conn.lastSyncedAt).toLocaleDateString()}`
                        : 'Never synced'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleSync(conn)}
                      disabled={syncing === conn.id}
                      title="Sync latest labs"
                      className="p-2 rounded-lg text-sky-600 hover:bg-sky-50 disabled:opacity-50 transition-colors"
                    >
                      <ArrowPathIcon
                        className={`h-4 w-4 ${syncing === conn.id ? 'animate-spin' : ''}`}
                      />
                    </button>
                    <button
                      onClick={() => handleDisconnect(conn)}
                      disabled={removing === conn.id}
                      title="Disconnect"
                      className="p-2 rounded-lg text-red-500 hover:bg-red-50 disabled:opacity-50 transition-colors"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}

          <p className="text-xs text-slate-400 mt-4">
            Your credentials are never stored by HealthClear AI. We use{' '}
            <a
              href="https://www.flexpa.com"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-slate-600"
            >
              Flexpa
            </a>
            , a HIPAA-compliant patient-authorized data exchange service.
          </p>
        </>
      )}
      {/* Privacy Disclosure Modal */}
      {showDisclosure && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <p className="text-3xl font-extrabold text-red-600 text-center mb-4 tracking-tight">
              Coming Soon
            </p>
            <h3 className="text-base font-semibold text-slate-800 mb-3">Before You Continue</h3>
            <p className="text-sm text-slate-600 mb-3">
              To connect your health provider, HealthClear AI uses{' '}
              <strong>Flexpa</strong>, a HIPAA-compliant data exchange service. Flexpa's login
              portal may use functional cookies to operate the sign-in flow.
            </p>
            <p className="text-sm text-slate-600 mb-3">
              <strong>
                These cookies are not used to track you or identify you across apps or websites.
              </strong>{' '}
              HealthClear AI does not use advertising identifiers or cross-site tracking. Your
              health data is used solely to provide you with lab analysis within this app.
            </p>
            <p className="text-sm text-slate-600 mb-5">
              Your EHR or insurance login credentials are never stored by HealthClear AI.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDisclosure(false)}
                className="flex-1 py-2.5 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={proceedWithFlexpa}
                className="flex-1 py-2.5 rounded-lg bg-sky-600 text-white text-sm font-medium hover:bg-sky-700 transition-colors"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
